import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './SanteMentale.css';
import { 
  FaBrain,
  FaHeartbeat,
  FaHandHoldingHeart,
  FaUsers,
  FaPhoneAlt,
  FaCalendarAlt,
  FaArrowLeft,
  FaCheckCircle,
  FaRegClock,
  FaSmile,
  FaRegSmile,
  FaWalking,
  FaMusic,
  FaBook,
  FaUserMd,
  FaCommentDots,
  FaShieldAlt,
  FaRegHeart,
  FaLeaf,
  FaSun,
  FaMoon,
  FaCoffee,
  FaYinYang,
  FaSpa,
  FaHandPeace,
  FaHeart,
  FaHandHolding
} from 'react-icons/fa';

const SanteMentale = () => {
  const navigate = useNavigate();

  const stressSigns = [
    {
      id: 1,
      sign: "Troubles du sommeil",
      description: "Difficultés à s'endormir, réveils nocturnes, fatigue persistante",
      icon: <FaMoon />
    },
    {
      id: 2,
      sign: "Anxiété constante",
      description: "Sentiment d'inquiétude permanent, palpitations, tensions",
      icon: <FaBrain />
    },
    {
      id: 3,
      sign: "Isolement social",
      description: "Retrait des relations sociales, perte d'intérêt pour les activités",
      icon: <FaUsers />
    },
    {
      id: 4,
      sign: "Difficultés de concentration",
      description: "Problèmes de mémoire, baisse des performances académiques",
      icon: <FaBook />
    },
    {
      id: 5,
      sign: "Changements d'humeur",
      description: "Irritabilité, tristesse persistante, sautes d'humeur",
      icon: <FaYinYang />
    },
    {
      id: 6,
      sign: "Troubles alimentaires",
      description: "Perte d'appétit ou compulsions alimentaires",
      icon: <FaCoffee />
    }
  ];

  const copingStrategies = [
    {
      id: 1,
      title: "Méditation et pleine conscience",
      icon: <FaSpa />,
      description: "Pratique quotidienne de 10-15 minutes pour réduire l'anxiété",
      benefits: ["Réduction du stress", "Meilleure concentration", "Stabilité émotionnelle"]
    },
    {
      id: 2,
      title: "Activité physique régulière",
      icon: <FaWalking />,
      description: "30 minutes d'exercice par jour pour libérer des endorphines",
      benefits: ["Amélioration de l'humeur", "Meilleur sommeil", "Énergie accrue"]
    },
    {
      id: 3,
      title: "Expression artistique",
      icon: <FaMusic />,
      description: "Dessin, musique, écriture pour exprimer ses émotions",
      benefits: ["Libération émotionnelle", "Créativité", "Bien-être"]
    },
    {
      id: 4,
      title: "Réseau de soutien",
      icon: <FaHandHolding />,
      description: "Maintenir des liens sociaux, partager ses difficultés",
      benefits: ["Soutien émotionnel", "Sentiment d'appartenance", "Échange d'expériences"]
    }
  ];

  const professionalHelp = [
    {
      id: 1,
      name: "Psychologue",
      description: "Écoute et accompagnement psychologique",
      when: "Difficultés émotionnelles, mal-être persistant",
      cost: "Gratuit en CMP, 30-60€ en libéral",
      where: "CMP, services universitaires, libéral",
      icon: <FaUserMd />
    },
    {
      id: 2,
      name: "Psychiatre",
      description: "Médecin spécialiste des troubles mentaux",
      when: "Troubles sévères, besoin de traitement médicamenteux",
      cost: "Remboursé par sécu (avec parcours coordonné)",
      where: "Hôpital, CMP, libéral",
      icon: <FaHeartbeat />
    },
    {
      id: 3,
      name: "Service de santé universitaire (SSU)",
      description: "Consultations gratuites pour étudiants",
      when: "Problèmes légers à modérés, premier recours",
      cost: "Gratuit",
      where: "Universités",
      icon: <FaHandHoldingHeart />
    },
    {
      id: 4,
      name: "Ligne d'écoute",
      description: "Soutien téléphonique 24/7",
      when: "Besoin immédiat, crise",
      cost: "Gratuit et anonyme",
      where: "3114 (numéro national)",
      icon: <FaPhoneAlt />
    }
  ];

  const resources = [
    {
      id: 1,
      name: "3114 - Numéro national de prévention du suicide",
      number: "3114",
      description: "Service gratuit 24h/24, 7j/7",
      available: "24/7"
    },
    {
      id: 2,
      name: "Fil Santé Jeunes",
      number: "0 800 235 236",
      description: "Écoute pour les 12-25 ans",
      available: "9h-23h"
    },
    {
      id: 3,
      name: "Nightline",
      number: "01 88 32 12 34",
      description: "Écoute étudiante nocturne",
      available: "20h-2h"
    },
    {
      id: 4,
      name: "SOS Amitié",
      number: "09 72 39 40 50",
      description: "Écoute et soutien psychologique",
      available: "24/7"
    }
  ];

  const selfCareTips = [
    {
      id: 1,
      tip: "Établir une routine",
      description: "Horaires réguliers pour sommeil, repas, études",
      icon: <FaRegClock />
    },
    {
      id: 2,
      tip: "Limiter les écrans",
      description: "Pause digitale, éviter écrans avant coucher",
      icon: <FaMoon />
    },
    {
      id: 3,
      tip: "Alimentation équilibrée",
      description: "Repas réguliers, hydratation, éviter excès",
      icon: <FaLeaf />
    },
    {
      id: 4,
      tip: "Temps pour soi",
      description: "Activités plaisir, hobbies, détente",
      icon: <FaSun />
    },
    {
      id: 5,
      tip: "Pratiquer la gratitude",
      description: "Noter 3 choses positives chaque jour",
      icon: <FaRegHeart />
    },
    {
      id: 6,
      tip: "Apprendre à dire non",
      description: "Poser ses limites, gérer sa charge mentale",
      icon: <FaShieldAlt />
    }
  ];

  const dailyRoutine = [
    {
      time: "Matin",
      activities: [
        "Se lever à heure fixe",
        "Petit-déjeuner équilibré",
        "5 min d'étirements",
        "Objectifs du jour"
      ]
    },
    {
      time: "Après-midi",
      activities: [
        "Pauses régulières",
        "Marche de 15 min",
        "Déjeuner loin des écrans",
        "Tâches prioritaires"
      ]
    },
    {
      time: "Soir",
      activities: [
        "Dîner léger",
        "Déconnexion écrans 1h avant coucher",
        "Rituel relaxant",
        "Coucher à heure régulière"
      ]
    }
  ];

  const warningSigns = [
    "Pensées suicidaires",
    "Auto-mutilation",
    "Consommation excessive d'alcool/drogues",
    "Isolement total",
    "Négligence de soi",
    "Crises d'angoisse répétées"
  ];

  const emergencyContacts = [
    {
      id: 1,
      name: "SAMU",
      number: "15",
      description: "Urgences médicales"
    },
    {
      id: 2,
      name: "Police Secours",
      number: "17",
      description: "Urgences"
    },
    {
      id: 3,
      name: "Numéro d'urgence européen",
      number: "112",
      description: "Urgences"
    }
  ];

  return (
    <div className="sm-page">
      {/* Hero Section */}
      <div className="sm-hero sm-hero--mental">
        <div className="sm-hero__background"></div>
        <div className="sm-hero__content">
          <button className="sm-back-button" onClick={() => navigate('/association')}>
            <FaArrowLeft /> Retour aux services
          </button>
          <div className="sm-hero__icon">
            <FaBrain />
          </div>
          <h1 className="sm-hero__title">Santé Mentale</h1>
          <p className="sm-hero__subtitle">Soutien psychologique et ressources pour le bien-être mental des étudiants</p>
        </div>
      </div>

      <div className="sm-content">
        <div className="sm-container">
          {/* Introduction */}
          <div className="sm-intro">
            <h2 className="sm-section-title">Prenez soin de votre santé mentale</h2>
            <p className="sm-intro__text">
              La santé mentale est essentielle pour réussir vos études et votre vie en France.
              Stress, anxiété, solitude... Les défis de l'expatriation étudiante peuvent être
              éprouvants. Notre service vous informe sur les ressources disponibles et vous
              accompagne vers un mieux-être.
            </p>
            <div className="sm-intro__quote">
              <FaRegSmile className="sm-intro__quote-icon" />
              <span>La santé mentale n'est pas une destination, mais un processus.</span>
            </div>
          </div>

          {/* Signes de stress */}
          <div className="sm-section">
            <h2 className="sm-section-title">Reconnaître les signes de stress</h2>
            <div className="sm-grid sm-grid--signs">
              {stressSigns.map((sign) => (
                <div className="sm-card sm-card--sign" key={sign.id}>
                  <div className="sm-card__icon">{sign.icon}</div>
                  <h3 className="sm-card__title">{sign.sign}</h3>
                  <p className="sm-card__description">{sign.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stratégies d'adaptation */}
          <div className="sm-section">
            <h2 className="sm-section-title">Stratégies pour mieux vivre</h2>
            <div className="sm-grid sm-grid--strategies">
              {copingStrategies.map((strategy) => (
                <div className="sm-card sm-card--strategy" key={strategy.id}>
                  <div className="sm-card__icon">{strategy.icon}</div>
                  <h3 className="sm-card__title">{strategy.title}</h3>
                  <p className="sm-card__description">{strategy.description}</p>
                  <div className="sm-card__benefits">
                    {strategy.benefits.map((benefit, i) => (
                      <div className="sm-benefit-item" key={i}>
                        <FaCheckCircle className="sm-benefit-item__check" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Aide professionnelle */}
          <div className="sm-section">
            <h2 className="sm-section-title">Consulter un professionnel</h2>
            <div className="sm-grid sm-grid--professional">
              {professionalHelp.map((help) => (
                <div className="sm-card sm-card--professional" key={help.id}>
                  <div className="sm-card__icon">{help.icon}</div>
                  <h3 className="sm-card__title">{help.name}</h3>
                  <p className="sm-card__description">{help.description}</p>
                  <div className="sm-card__details">
                    <div className="sm-detail-item">
                      <strong>Quand consulter ?</strong>
                      <span>{help.when}</span>
                    </div>
                    <div className="sm-detail-item">
                      <strong>Coût</strong>
                      <span>{help.cost}</span>
                    </div>
                    <div className="sm-detail-item">
                      <strong>Où trouver ?</strong>
                      <span>{help.where}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Auto-soins */}
          <div className="sm-section">
            <h2 className="sm-section-title">Pratiques d'auto-soin</h2>
            <div className="sm-grid sm-grid--selfcare">
              {selfCareTips.map((tip) => (
                <div className="sm-card sm-card--selfcare" key={tip.id}>
                  <div className="sm-card__icon">{tip.icon}</div>
                  <h3 className="sm-card__title">{tip.tip}</h3>
                  <p className="sm-card__description">{tip.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Routine quotidienne */}
          <div className="sm-section">
            <h2 className="sm-section-title">Exemple de routine équilibrée</h2>
            <div className="sm-grid sm-grid--routine">
              {dailyRoutine.map((period, index) => (
                <div className="sm-routine-card" key={index}>
                  <h3 className="sm-routine-card__title">{period.time}</h3>
                  <ul className="sm-routine-card__list">
                    {period.activities.map((activity, i) => (
                      <li key={i}>
                        <FaCheckCircle className="sm-routine-card__check" />
                        <span>{activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Ressources d'écoute */}
          <div className="sm-section">
            <h2 className="sm-section-title">Ressources d'écoute</h2>
            <div className="sm-grid sm-grid--resources">
              {resources.map((resource) => (
                <div className="sm-card sm-card--resource" key={resource.id}>
                  <div className="sm-card__resource-number">{resource.number}</div>
                  <h3 className="sm-card__title">{resource.name}</h3>
                  <p className="sm-card__description">{resource.description}</p>
                  <div className="sm-card__availability">
                    <FaRegClock className="sm-card__availability-icon" />
                    <span>Disponible : {resource.available}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Signes d'alerte */}
          <div className="sm-section">
            <h2 className="sm-section-title">Signes d'alerte à ne pas ignorer</h2>
            <div className="sm-warning-grid">
              {warningSigns.map((sign, index) => (
                <div className="sm-warning-item" key={index}>
                  <FaShieldAlt className="sm-warning-item__icon" />
                  <span>{sign}</span>
                </div>
              ))}
            </div>
            <div className="sm-warning-message">
              <FaHeart className="sm-warning-message__icon" />
              <p>Si vous ou un proche présentez ces signes, consultez immédiatement un professionnel ou appelez le 3114.</p>
            </div>
          </div>

          {/* Urgences */}
          <div className="sm-section">
            <h2 className="sm-section-title">Numéros d'urgence</h2>
            <div className="sm-grid sm-grid--emergency">
              {emergencyContacts.map((contact) => (
                <div className="sm-card sm-card--emergency" key={contact.id}>
                  <div className="sm-card__emergency-number">{contact.number}</div>
                  <h3 className="sm-card__title">{contact.name}</h3>
                  <p className="sm-card__description">{contact.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="sm-contact">
            <h2 className="sm-section-title sm-section-title--centered">Besoin de soutien ?</h2>
            <p className="sm-contact__text">Nous pouvons vous orienter vers les ressources adaptées à votre situation</p>
            <div className="sm-contact-buttons">
              <Link to="/contact" className="sm-button sm-button--primary">
                <FaCommentDots /> Échanger avec un conseiller
              </Link>
              <Link to="/rendez-vous" className="sm-button sm-button--secondary">
                <FaCalendarAlt /> Prendre rendez-vous
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SanteMentale;